import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { pdf } from '@react-pdf/renderer';
import { 
  Wallet as WalletIcon, ArrowLeft, Plus, TrendingUp, TrendingDown, 
  FileText, Download, Calendar, CreditCard, DollarSign, Bot, Settings, LogOut, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../translations';
import LanguageToggle from './LanguageToggle';
import InvoicePDF from './InvoicePDF';
import { API_URL } from '../utils/api';

const Wallet = () => {
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const { t } = useTranslation(language);
  
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [walletRes, transRes, invoicesRes, subRes, tokenRes] = await Promise.all([
        axios.get(`${API_URL}/billing/wallet`),
        axios.get(`${API_URL}/billing/transactions?limit=10`),
        axios.get(`${API_URL}/billing/invoices?limit=10`),
        axios.get(`${API_URL}/billing/subscription`),
        axios.get(`${API_URL}/billing/token-usage`)
      ]);

      setWallet(walletRes.data.wallet);
      setTransactions(transRes.data.transactions);
      setInvoices(invoicesRes.data.invoices);
      setSubscription(subRes.data.subscription);
      setTokenUsage(tokenRes.data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast.error(t('wallet.fetchError') || 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!depositAmount || depositAmount <= 0) {
      toast.error(t('wallet.invalidAmount') || 'Please enter a valid amount');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/billing/wallet/deposit`, {
        amount: parseInt(depositAmount),
        paymentMethod: 'bank'
      });
      
      // Show message that payment is required
      if (response.data.paymentRequired) {
        toast.success(
          t('wallet.paymentPending') || 
          'Deposit request created. Please complete payment to add funds to your wallet.',
          { duration: 5000 }
        );
        toast(
          t('wallet.awaitingPayment') || 
          '⏳ Awaiting payment gateway integration. Funds will be added after payment verification.',
          { duration: 6000, icon: '💳' }
        );
      } else {
        toast.success(t('wallet.depositSuccess') || 'Deposit successful!');
      }
      
      setShowDepositModal(false);
      setDepositAmount('');
      fetchWalletData();
    } catch (error) {
      console.error('Error depositing:', error);
      toast.error(error.response?.data?.error || t('wallet.depositError') || 'Deposit failed');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'fa' ? 'fa-IR' : 'en-US').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'fa' ? 'fa-IR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadInvoicePDF = async (invoice) => {
    try {
      const blob = await pdf(<InvoicePDF invoice={invoice} user={user} language={language} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(
        language === 'fa' ? 'فاکتور با موفقیت دانلود شد' : 'Invoice downloaded successfully'
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(
        language === 'fa' ? 'خطا در تولید فاکتور' : 'Error generating invoice'
      );
    }
  };

  const continuePayment = async (invoice) => {
    try {
      toast.loading(
        language === 'fa' ? 'در حال انتقال به درگاه پرداخت...' : 'Redirecting to payment gateway...',
        { id: 'payment' }
      );
      
      // For now, simulate payment success after 2 seconds
      // In real implementation, this would redirect to payment gateway
      setTimeout(async () => {
        try {
          const response = await axios.post(`${API_URL}/billing/wallet/verify-payment`, {
            invoice_id: invoice.id,
            payment_status: 'success',
            transaction_id: `txn_${Date.now()}`
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          if (response.data.success) {
            toast.success(
              language === 'fa' ? 'پرداخت با موفقیت انجام شد' : 'Payment completed successfully',
              { id: 'payment' }
            );
            fetchWalletData(); // Refresh data
          } else {
            throw new Error(response.data.message);
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          toast.error(
            language === 'fa' ? 'خطا در تایید پرداخت' : 'Payment verification failed',
            { id: 'payment' }
          );
        }
      }, 2000);
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(
        language === 'fa' ? 'خطا در پردازش پرداخت' : 'Payment processing error',
        { id: 'payment' }
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Ultra-Glassy Header - Fixed to top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/50 backdrop-blur-3xl border-b border-white/30 shadow-2xl shadow-gray-300/20" style={{backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)'}}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className={`flex items-center gap-2 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 sm:p-2 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all backdrop-blur-sm"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {t('wallet.title') || 'Wallet & Billing'}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  {t('wallet.subtitle') || 'Manage your balance and subscriptions'}
                </p>
              </div>
            </div>
            <div className={`flex items-center gap-1 sm:gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LanguageToggle />
              <Link
                to="/plans"
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg sm:rounded-xl transition-all text-xs font-semibold shadow-lg shadow-blue-500/25"
              >
                {t('wallet.upgradePlan') || 'Upgrade Plan'}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16 sm:h-20"></div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 md:py-12">
        {/* Balance Card */}
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 text-white mb-6 sm:mb-8 shadow-2xl shadow-blue-500/25 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/5 rounded-full -translate-y-12 sm:-translate-y-16 translate-x-12 sm:translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 sm:w-24 sm:h-24 bg-white/5 rounded-full translate-y-8 sm:translate-y-12 -translate-x-8 sm:-translate-x-12"></div>
          <div className="relative flex justify-between items-center">
            <div className="flex-1 min-w-0 text-left" style={{textAlign: language === 'fa' ? 'right' : 'left'}}>
              <p className="text-blue-100 mb-1 sm:mb-2 text-xs sm:text-sm font-medium">{t('wallet.currentBalance') || 'Current Balance'}</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent break-words">
                {formatCurrency(wallet?.balance || 0)} {language === 'fa' ? 'ریال' : 'IRR'}
              </h2>
              <p className="text-blue-100/80 text-xs sm:text-sm">
                {t('wallet.lastUpdated') || 'Last updated'}: {formatDate(wallet?.updated_at)}
              </p>
            </div>
            <button
              onClick={() => setShowDepositModal(true)}
              className="flex-shrink-0 flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg sm:rounded-xl font-semibold hover:bg-white/30 transition-all border border-white/20 text-xs"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{t('wallet.addFunds') || 'Add Funds'}</span>
            </button>
          </div>
        </div>

        {/* Subscription Card */}
        {subscription && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 p-4 sm:p-6 mb-6 sm:mb-8 shadow-xl shadow-gray-300/10">
            <div className={`flex justify-between items-start mb-3 sm:mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1">
                  {t('wallet.activePlan') || 'Active Plan'}
                </h3>
                <p className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {language === 'fa' ? subscription.name_fa : subscription.name}
                </p>
              </div>
              <span className="px-2 sm:px-4 py-1 sm:py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold border border-green-200">
                {t('wallet.active') || 'Active'}
              </span>
            </div>
            
            {tokenUsage?.subscription && (
              <div className="mb-4">
                <div className={`flex justify-between text-sm mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-gray-600">{t('wallet.tokensUsed') || 'Tokens Used'}</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(tokenUsage?.subscription?.tokensUsed || 0)} / {formatCurrency(tokenUsage?.subscription?.tokensLimit || 0)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(tokenUsage?.subscription?.percentageUsed || 0, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {tokenUsage?.subscription?.percentageUsed || 0}% {t('wallet.used') || 'used'}
                </p>
              </div>
            )}

            <div className="flex gap-4 text-sm">
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Bot className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">
                  {subscription.max_bots === -1 ? (t('wallet.unlimited') || 'Unlimited') : subscription.max_bots} {t('wallet.bots') || 'Bots'}
                </span>
              </div>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">
                  {t('wallet.renewsOn') || 'Renews on'}: {formatDate(subscription.ends_at)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className={`flex gap-1 sm:gap-2 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-1 sm:p-2 border border-white/30 shadow-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
            {['overview', 'transactions', 'invoices', 'usage'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-2 sm:px-3 md:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all text-xs ${
                  activeTab === tab
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {t(`wallet.tabs.${tab}`) || tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'transactions' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 overflow-hidden shadow-xl shadow-gray-300/10">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/30 bg-gradient-to-r from-gray-50/50 to-white/50">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {t('wallet.recentTransactions') || 'Recent Transactions'}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {(transactions || []).length === 0 ? (
                <div className="p-6 sm:p-8 text-center text-gray-500 text-sm sm:text-base">
                  {t('wallet.noTransactions') || 'No transactions yet'}
                </div>
              ) : (
                (transactions || []).map((tx) => (
                  <div key={tx.id} className={`p-4 sm:p-6 flex flex-col sm:flex-row sm:justify-between sm:items-center hover:bg-gray-50 gap-3 sm:gap-0 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {tx.type === 'credit' ? (
                          <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{tx.description}</p>
                        <p className="text-xs sm:text-sm text-gray-500">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <div className={`flex items-center justify-between sm:justify-end gap-2 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                      <div className="text-left sm:text-right">
                        <p className={`font-bold text-base sm:text-lg ${
                          tx.type === 'credit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)} {language === 'fa' ? 'ریال' : 'IRR'}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {t('wallet.balance') || 'Balance'}: {formatCurrency(tx.balance_after)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 overflow-hidden shadow-xl shadow-gray-300/10">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/30 bg-gradient-to-r from-gray-50/50 to-white/50">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {t('wallet.invoices') || 'Invoices'}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {(invoices || []).length === 0 ? (
                <div className="p-6 sm:p-8 text-center text-gray-500 text-sm sm:text-base">
                  {t('wallet.noInvoices') || 'No invoices yet'}
                </div>
              ) : (
                (invoices || []).map((invoice) => (
                  <div key={invoice.id} className={`p-4 sm:p-6 flex flex-col sm:flex-row sm:justify-between sm:items-center hover:bg-gray-50 gap-3 sm:gap-0 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{invoice.invoice_number}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{invoice.description}</p>
                        <p className="text-xs sm:text-sm text-gray-500">{formatDate(invoice.created_at)}</p>
                      </div>
                    </div>
                    <div className={`flex items-center justify-between sm:justify-end gap-2 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                      <div className="text-left sm:text-right">
                        <p className="font-bold text-base sm:text-lg text-gray-900">
                          {formatCurrency(invoice.amount)} {language === 'fa' ? 'ریال' : 'IRR'}
                        </p>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          invoice.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : invoice.status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {invoice.status === 'paid' 
                            ? (t('wallet.paid') || 'Paid')
                            : invoice.status === 'failed'
                            ? (t('wallet.failed') || 'Failed')
                            : (t('wallet.pending') || 'Pending')
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        {invoice.status === 'paid' && (
                          <button
                            onClick={() => downloadInvoicePDF(invoice)}
                            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title={language === 'fa' ? 'دانلود فاکتور' : 'Download Invoice'}
                          >
                            <Download className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                          </button>
                        )}
                        {invoice.status === 'pending' && (
                          <button
                            onClick={() => continuePayment(invoice)}
                            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                          >
                            <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">
                              {language === 'fa' ? 'ادامه پرداخت' : 'Continue Payment'}
                            </span>
                          </button>
                        )}
                        {invoice.status === 'failed' && (
                          <button
                            onClick={() => downloadInvoicePDF(invoice)}
                            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">
                              {language === 'fa' ? 'دانلود فاکتور' : 'Download Invoice'}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'usage' && tokenUsage && (
          <div className="space-y-6">
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 p-4 sm:p-6 shadow-xl shadow-gray-300/10">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                {t('wallet.tokenUsage') || 'Token Usage Statistics'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">{t('wallet.totalTokens') || 'Total Tokens'}</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">
                    {formatCurrency(tokenUsage?.totalUsage?.tokens || 0)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">{t('wallet.totalCost') || 'Total Cost'}</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">
                    {formatCurrency(tokenUsage?.totalUsage?.cost || 0)} {language === 'fa' ? 'ریال' : 'IRR'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 overflow-hidden shadow-xl shadow-gray-300/10">
              <div className="px-6 py-4 border-b border-white/30 bg-gradient-to-r from-gray-50/50 to-white/50">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('wallet.usageByBot') || 'Usage by Chatbot'}
                </h3>
              </div>
              <div className="divide-y divide-gray-200">
                {(tokenUsage?.usageByBot || []).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    {t('wallet.noUsageData') || 'No usage data yet'}
                  </div>
                ) : (
                  (tokenUsage?.usageByBot || []).map((bot, index) => (
                    <div key={index} className={`p-6 flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div>
                        <p className="font-medium text-gray-900">{bot.name}</p>
                        <p className="text-sm text-gray-500">{bot.slug}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(bot?.tokens || 0)} {t('wallet.tokens') || 'tokens'}
                        </p>
                        {(bot?.cost || 0) > 0 && (
                          <p className="text-sm text-red-600">
                            {formatCurrency(bot?.cost || 0)} {language === 'fa' ? 'ریال' : 'IRR'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Stats */}
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 p-6 shadow-xl shadow-gray-300/10">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('wallet.quickStats') || 'Quick Stats'}
              </h3>
              <div className="space-y-4">
                <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-gray-600">{t('wallet.totalSpent') || 'Total Spent'}</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(
                      (transactions || [])
                        .filter(tx => tx.type === 'debit')
                        .reduce((sum, tx) => sum + tx.amount, 0)
                    )} {language === 'fa' ? 'ریال' : 'IRR'}
                  </span>
                </div>
                <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-gray-600">{t('wallet.totalDeposits') || 'Total Deposits'}</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(
                      (transactions || [])
                        .filter(tx => tx.type === 'credit')
                        .reduce((sum, tx) => sum + tx.amount, 0)
                    )} {language === 'fa' ? 'ریال' : 'IRR'}
                  </span>
                </div>
                <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-gray-600">{t('wallet.totalInvoices') || 'Total Invoices'}</span>
                  <span className="font-semibold text-gray-900">{(invoices || []).length}</span>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/30 p-6 shadow-xl shadow-gray-300/10">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('wallet.recentActivity') || 'Recent Activity'}
              </h3>
              <div className="space-y-3">
                {(transactions || []).slice(0, 5).map((tx) => (
                  <div key={tx.id} className={`flex justify-between items-center text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-gray-600 truncate">{tx.description}</span>
                    <span className={`font-medium ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/30">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('wallet.addFunds') || 'Add Funds'}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('wallet.depositDescription') || 'Enter the amount you want to add to your wallet'}
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('wallet.amount') || 'Amount'} ({language === 'fa' ? 'ریال' : 'IRR'})
              </label>
              <input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={language === 'fa' ? '۱۰۰۰۰۰۰' : '1000000'}
              />
              <p className="text-sm text-gray-500 mt-2">
                {t('wallet.minimumDeposit') || 'Minimum deposit'}: {formatCurrency(100000)} {language === 'fa' ? 'ریال' : 'IRR'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDepositModal(false);
                  setDepositAmount('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeposit}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all"
              >
                {t('wallet.confirm') || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;

